import { EpicIcons } from "@constellation-academy/epic-ui";
import { graphql, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { useDialogLogic } from "@hooks/use-dialog-logic";
import { useTranslation } from "@i18n/hooks";
import { type StartPublishingButton_StartPublishingV2Mutation } from "@relay/StartPublishingButton_StartPublishingV2Mutation.graphql";

const START_PUBLISHING_MUTATION = graphql`
	mutation StartPublishingButton_StartPublishingV2Mutation($input: StartPublishingV2Input!) {
		Admin {
			PublishingV2 {
				startPublishingV2(input: $input) {
					tree {
						...educationalOffer_TreeFragment
					}
					publishing {
						...educationalOffer_PublishingV2Fragment
					}
				}
			}
		}
	}
`;

const PUBLISHING_ALREADY_RUNNING = "publishingv2_publishing_already_running";

type OwnProps = {
	rootNodeId: string;
	status: "Draft" | "PTO" | "PTG";
	hasBeenPublishedToGroupOnce: boolean;
	isPublishing?: boolean;
	onPublishingFailed: () => void;
};

export const StartPublishingButton = ({
	rootNodeId,
	status,
	hasBeenPublishedToGroupOnce,
	isPublishing,
	onPublishingFailed,
}: OwnProps) => {
	const { t } = useTranslation("common");
	const [startPublishing, isStartingPublishing] =
		useMutation<StartPublishingButton_StartPublishingV2Mutation>(START_PUBLISHING_MUTATION);
	const { showDialog, dialogComponent } = useDialogLogic();

	const handlePublish = (target: "OWNER" | "GROUP") => {
		const labelKey =
			target === "OWNER"
				? "educationalOffer.publishToOwner"
				: "educationalOffer.publishToGroup";

		showDialog({
			title: t(labelKey),
			content: t("educationalOffer.publishingIrreversible"),
			affirmativeText: t("actions.publish"),
			negativeText: t("actions.cancel"),
			dialogCallback: (result) => {
				if (result === "Accept") {
					startPublishing({
						variables: {
							input: {
								rootNodeId,
								target,
							},
						},
						updater: (store) => {
							const publishing = store
								.getRootField("Admin")
								?.getLinkedRecord("PublishingV2")
								?.getLinkedRecord("startPublishingV2", {
									input: { rootNodeId, target },
								})
								?.getLinkedRecord("publishing");
							if (!publishing) return;
							store
								.getRoot()
								.getLinkedRecord("Admin")
								?.getLinkedRecord("PublishingV2")
								?.setLinkedRecord(publishing, "ActivePublishing", {
									rootId: rootNodeId,
								});
						},
						onError: (error) => {
							if (error.message.includes(PUBLISHING_ALREADY_RUNNING)) return;
							toast.error(t("educationalOffer.publishingError"));
							onPublishingFailed();
						},
					});
				}
			},
		});
	};

	if (isPublishing || isStartingPublishing) {
		return (
			<Button
				disabled
				loading
				variant={ButtonVariant.Default}
				label={t("educationalOffer.publishingInProgress")}
			/>
		);
	}

	if (status === "PTG") return null;

	if (hasBeenPublishedToGroupOnce || status === "PTO") {
		return (
			<>
				{dialogComponent}
				<Button
					icon={EpicIcons.SEND}
					variant={ButtonVariant.Strong}
					label={t("educationalOffer.publishToGroup")}
					onClick={() => handlePublish("GROUP")}
				/>
			</>
		);
	}

	// Draft with no prior PTG publish: show both options
	return (
		<>
			{dialogComponent}
			<Button
				icon={EpicIcons.SEND}
				variant={ButtonVariant.Default}
				label={t("educationalOffer.publishToOwner")}
				onClick={() => handlePublish("OWNER")}
			/>
			<Button
				icon={EpicIcons.SEND}
				variant={ButtonVariant.Strong}
				label={t("educationalOffer.publishToGroup")}
				onClick={() => handlePublish("GROUP")}
			/>
		</>
	);
};
